import { useMatch } from 'app/routx-router'
import Text from 'app/src/kit/Text'
import isNil from 'lodash-es/isNil'
import { styled } from 'styled-components'
import CountIndicator from './CountIndicator'
import { IconComponent, IconProps } from './Icon'
import Link, { LinkProps } from './LinkComponent'
import { interactiveBackgroundHover } from './mixins/interactive'

const Container = styled(Flex)<{ $isActive: boolean }>`
  ${interactiveBackgroundHover}

  align-items: center;
  justify-content: space-between;

  padding: ${(p) => `${p.theme.spacings.sm}px`};
  margin: ${(p) => `${p.theme.spacings.xs}px 0px`};

  height: 26px;

  border-radius: ${(p) => `${p.theme.radiuses.md}px`};
  background-color: ${({ $isActive: isActive, theme }) =>
    isActive ? theme.colors.primary[200] : 'transparent'};
`

const defaultIconProps: Partial<IconProps> = {
  $size: 'lg'
}

export interface SidePanelLinkProps extends Omit<LinkProps, 'active'> {
  LeftIcon?: IconComponent | null
  iconProps?: IconProps
  label: string
  matchExactPath?: boolean
  description?: string
  indicator?: string | number
  isActive?: boolean
}

const SidePanelLink = ({
  LeftIcon = null,
  iconProps,
  label,
  onActive,
  to,
  matchExactPath = false,
  indicator,
  isActive: isActiveFromProps,
  ...linkProps
}: SidePanelLinkProps) => {
  const matchPattern = `${to}${matchExactPath ? '' : '/*'}`
  // @ts-expect-error This work because of call site but is ambiguous
  const newActive = useMatch(matchPattern)
  const isActive = !isNil(isActiveFromProps) ? isActiveFromProps : !isNil(newActive)

  const iconColor = isActive
    ? 'primary.500'
    : !isNil(iconProps?.$color)
      ? iconProps?.$color
      : 'grayscale.black'

  return (
    <Link active={isActive} onActive={onActive} to={to} {...linkProps}>
      <Container $isActive={isActive}>
        <Flex $alignItems='center' $gap='md' $flex={1} $width={'100%'}>
          {!isNil(LeftIcon) ? (
            <LeftIcon {...defaultIconProps} {...iconProps} $color={iconColor} />
          ) : null}
          <Box $flex={1} $minWidth={0}>
            <Text
              $ellipsis
              $color={isActive ? 'primary.500' : undefined}
              $fontSize='sm'
              $fontWeight='medium'
            >
              {label}
            </Text>
          </Box>
          {!isNil(indicator) ? (
            <CountIndicator
              backgroundColor={indicator === 0 ? 'grayscale.100' : 'primary.500'}
              color={indicator === 0 ? 'grayscale.700' : 'grayscale.white'}
              count={indicator}
            />
          ) : null}
        </Flex>
      </Container>
    </Link>
  )
}

export default SidePanelLink
